import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { AnyRow } from "@/lib/rowTypes";

export interface ContrastPair {
  id: string;
  term_a: string;
  term_b: string;
  explanation: string;
  course_name: string | null;
  priority?: number;
  term_family?: string | null;
}

export interface DueReviewTerm {
  term: string;
  confidence_level: number;
  wrong_answer_triggered: boolean;
}

export function useVocabReinforcement(courseName?: string) {
  const [contrastPairs, setContrastPairs] = useState<ContrastPair[]>([]);
  const [dueTerms, setDueTerms] = useState<DueReviewTerm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Load contrast pairs for this course (or all)
      let query = supabase.from("vocab_contrast_pairs").select("*").order("sort_order");
      if (courseName) {
        query = query.or(`course_name.eq.${courseName},course_name.is.null`);
      }
      const { data: pairs } = await query;
      setContrastPairs((pairs || []).map((p: AnyRow) => ({
        id: p.id,
        term_a: p.term_a,
        term_b: p.term_b,
        explanation: p.explanation,
        course_name: p.course_name,
        priority: p.priority,
        term_family: p.term_family,
      })));

      // Load due vocab review terms
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: dueData } = await supabase
          .from("vocab_review_history")
          .select("term, confidence_level, wrong_answer_triggered")
          .eq("user_id", session.user.id)
          .lte("next_review_at", new Date().toISOString())
          .order("confidence_level", { ascending: true })
          .limit(10);
        setDueTerms((dueData || []).map((d: AnyRow) => ({
          term: d.term,
          confidence_level: d.confidence_level || 0,
          wrong_answer_triggered: d.wrong_answer_triggered || false,
        })));
      }

      setLoading(false);
    };
    void load();
  }, [courseName]);

  const markReviewed = useCallback(async (term: string, confident: boolean) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const newConfidence = confident ? 3 : 1;
    const intervalDays = confident ? 7 : 1;

    await supabase.from("vocab_review_history").upsert({
      user_id: session.user.id,
      term,
      review_count: 1,
      last_reviewed_at: new Date().toISOString(),
      next_review_at: new Date(Date.now() + intervalDays * 86400000).toISOString(),
      confidence_level: newConfidence,
      wrong_answer_triggered: !confident,
    }, { onConflict: "user_id,term" });

    setDueTerms(prev => prev.filter(t => t.term !== term));
  }, []);

  const trackWrongAnswer = useCallback(async (term: string, contrastTerm?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Schedule resurfacing in 1 day with wrong_answer flag
    await supabase.from("vocab_review_history").upsert({
      user_id: session.user.id,
      term,
      review_count: 1,
      last_reviewed_at: new Date().toISOString(),
      next_review_at: new Date(Date.now() + 86400000).toISOString(),
      confidence_level: 0,
      wrong_answer_triggered: true,
      contrast_pair_shown: !!contrastTerm,
    }, { onConflict: "user_id,term" });

    // Also schedule the contrast term if provided
    if (contrastTerm) {
      await supabase.from("vocab_review_history").upsert({
        user_id: session.user.id,
        term: contrastTerm,
        review_count: 1,
        last_reviewed_at: new Date().toISOString(),
        next_review_at: new Date(Date.now() + 86400000).toISOString(),
        confidence_level: 0,
        wrong_answer_triggered: true,
        contrast_pair_shown: true,
      }, { onConflict: "user_id,term" });
    }
  }, []);

  return { contrastPairs, dueTerms, loading, markReviewed, trackWrongAnswer };
}
